import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { generateTempId } from '../lib/offline/offlineDb';
import { useOfflineMutation } from './useOfflineMutation';
import type { Favorite } from './useFavorites';

export interface FavoriteGroup {
  id: number | string;
  name: string;
  favorites: Favorite[];
  created_at: string;
}

export function useFavoriteGroups(enabled = true) {
  return useQuery<FavoriteGroup[]>({
    queryKey: ['favorite-groups'],
    queryFn: () => api<FavoriteGroup[]>('/favorite-groups'),
    enabled,
    // Only poll while online — see VIVATISPDB-305.
    refetchInterval: () => (typeof navigator !== 'undefined' && navigator.onLine ? 5 * 60 * 1000 : false),
  });
}

export function useCreateFavoriteGroup() {
  const queryClient = useQueryClient();

  return useOfflineMutation<string, FavoriteGroup | void>({
    operationType: 'FAVORITE_GROUP_CREATE',
    feature: 'favorites',
    needsTempId: true,
    mutationFn: (name) =>
      api<FavoriteGroup>('/favorite-groups', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    toPayload: (name) => ({ name }),
    invalidateKeys: [['favorite-groups']],
    onMutate: async (name, tempId) => {
      const id = tempId ?? generateTempId();
      const newGroup: FavoriteGroup = { id, name, favorites: [], created_at: new Date().toISOString() };
      queryClient.setQueryData<FavoriteGroup[]>(['favorite-groups'], (old = []) => [...old, newGroup]);
      return { previous: undefined };
    },
  });
}

export function useRenameFavoriteGroup() {
  const queryClient = useQueryClient();

  return useOfflineMutation<{ groupId: number | string; name: string }>({
    operationType: 'FAVORITE_GROUP_RENAME',
    feature: 'favorites',
    mutationFn: async ({ groupId, name }) => {
      await api(`/favorite-groups/${groupId}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
      });
    },
    toPayload: ({ groupId, name }) => ({ groupId, name }),
    invalidateKeys: [['favorite-groups']],
    onMutate: async ({ groupId, name }) => {
      const previous = queryClient.getQueryData<FavoriteGroup[]>(['favorite-groups']);
      queryClient.setQueryData<FavoriteGroup[]>(['favorite-groups'], (old = []) =>
        old.map((g) => (g.id === groupId ? { ...g, name } : g)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['favorite-groups'], context.previous);
      }
    },
  });
}

export function useDeleteFavoriteGroup() {
  const queryClient = useQueryClient();

  return useOfflineMutation<number | string>({
    operationType: 'FAVORITE_GROUP_DELETE',
    feature: 'favorites',
    mutationFn: async (groupId) => {
      await api(`/favorite-groups/${groupId}`, { method: 'DELETE' });
    },
    toPayload: (groupId) => ({ groupId }),
    invalidateKeys: [['favorite-groups']],
    onMutate: async (groupId) => {
      const previous = queryClient.getQueryData<FavoriteGroup[]>(['favorite-groups']);
      queryClient.setQueryData<FavoriteGroup[]>(['favorite-groups'], (old = []) =>
        old.filter((g) => g.id !== groupId),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['favorite-groups'], context.previous);
      }
    },
  });
}

export function useAddFavoriteToGroup() {
  const queryClient = useQueryClient();

  return useOfflineMutation<{ groupId: number; favoriteId: number | string }>({
    operationType: 'FAVORITE_GROUP_MEMBER_ADD',
    feature: 'favorites',
    mutationFn: async ({ groupId, favoriteId }) => {
      await api(`/favorite-groups/${groupId}/favorites`, {
        method: 'POST',
        body: JSON.stringify({ favorite_id: favoriteId }),
      });
    },
    toPayload: ({ groupId, favoriteId }) => ({ groupId, favoriteId }),
    invalidateKeys: [['favorite-groups'], ['favorites']],
    onMutate: async ({ groupId, favoriteId }) => {
      const previous = queryClient.getQueryData<FavoriteGroup[]>(['favorite-groups']);
      const allFavorites = queryClient.getQueryData<import('./useFavorites').Favorite[]>(['favorites']) ?? [];
      const favorite = allFavorites.find((f) => f.id === favoriteId || f.product_id === Number(favoriteId));
      if (favorite) {
        queryClient.setQueryData<FavoriteGroup[]>(['favorite-groups'], (old = []) =>
          old.map((g) => {
            if (g.id !== groupId) return g;
            const alreadyIn = g.favorites.some((f) => f.id === favorite.id);
            if (alreadyIn) return g;
            return { ...g, favorites: [...g.favorites, favorite] };
          }),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['favorite-groups'], context.previous);
      }
    },
  });
}

export function useRemoveFavoriteFromGroup() {
  const queryClient = useQueryClient();

  return useOfflineMutation<{ groupId: number; favoriteId: number | string }>({
    operationType: 'FAVORITE_GROUP_MEMBER_REMOVE',
    feature: 'favorites',
    mutationFn: async ({ groupId, favoriteId }) => {
      await api(`/favorite-groups/${groupId}/favorites/${favoriteId}`, { method: 'DELETE' });
    },
    toPayload: ({ groupId, favoriteId }) => ({ groupId, favoriteId }),
    invalidateKeys: [['favorite-groups']],
    onMutate: async ({ groupId, favoriteId }) => {
      const previous = queryClient.getQueryData<FavoriteGroup[]>(['favorite-groups']);
      queryClient.setQueryData<FavoriteGroup[]>(['favorite-groups'], (old = []) =>
        old.map((g) => {
          if (g.id !== groupId) return g;
          return { ...g, favorites: g.favorites.filter((f) => f.id !== favoriteId) };
        }),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['favorite-groups'], context.previous);
      }
    },
  });
}
